/**
 * The six `builder_user_access` flags — MOVED here unchanged.
 *
 * Lifted from `UserPermissionsPage` because that page is retired and
 * this is the only screen a person is administered from. Nothing about
 * it changed: the flags are saved one PUT per toggle, an admin's are all
 * true and the controls are disabled with the note that says why (the
 * role bypasses `require_builder_perm`, so a toggle would be a no-op).
 *
 * It stays PER PERSON and never becomes a group field. This is
 * PervasiveMind staff access to the BUILDER; groups govern what a tenant
 * person sees in the PORTAL. A group that could carry these flags would
 * let a tenant-facing object grant entry to the admin console.
 */

import { Spinner } from "@pervasivemind/bi-components";
import { Wrench } from "lucide-react";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  BUILDER_ACCESS_FLAGS,
  adminUsersApi,
  type BuilderAccessFlags,
  type UserAdmin,
} from "@/lib/admin-users-api";

export function BuilderAccessCard({
  user,
  onError,
}: {
  user: UserAdmin;
  onError: (message: string) => void;
}): JSX.Element {
  const { t } = useT();
  const isAdmin = user.role === "admin";
  const [flags, setFlags] = useState<BuilderAccessFlags | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // A role change re-reads the flags: an admin's GET is all-true BY
    // ROLE, and a toggle computed from that copy after a demotion would
    // PUT five flags the stored row never had.
    setFlags(null);
    void adminUsersApi
      .getBuilderAccess(user.id)
      .then((res) => {
        if (!cancelled) setFlags(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          onError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user.id, user.role, onError]);

  const toggle = async (flag: keyof BuilderAccessFlags): Promise<void> => {
    if (flags === null || isAdmin) return;
    setSaving(true);
    try {
      setFlags(
        await adminUsersApi.putBuilderAccess(user.id, {
          ...flags,
          [flag]: !flags[flag],
        }),
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900">
        <Wrench size={13} className="text-neutral-500" />
        {t("adminUsers.perms.builderAccessTitle")}
      </h3>
      <p className="text-xs text-neutral-500">
        {isAdmin
          ? t("adminUsers.perms.builderAdminNote")
          : t("adminUsers.perms.builderAccessHint")}
      </p>
      {flags === null ? (
        <span className="inline-flex items-center gap-2 text-xs text-neutral-500">
          <Spinner size={12} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </span>
      ) : (
        <div className="space-y-1.5 pt-1">
          {BUILDER_ACCESS_FLAGS.map((flag) => (
            <label
              key={flag}
              className={`flex items-center gap-2 text-sm ${
                isAdmin ? "opacity-60" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={flags[flag]}
                onChange={() => void toggle(flag)}
                disabled={isAdmin || saving}
                className="h-4 w-4 rounded border-neutral-300"
              />
              <span>{t(`adminUsers.builderFlag.${flag}`)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
